<template>
  <div
    class="e-adaptive-media"
    :class="[
      { 'as-bg': isBackground || ratio },
      { 'has-radius': showImageRedius },
      { controls: hasControls },
    ]"
    v-bind="$attrs"
    v-if="videoSrc || desktopImageSrc || mobileImageSrc"
    :style="rootStyle"
    ref="rootEl"
  >
    <video-player
      class="e-adaptive-media__video"
      v-if="!$isNoE(videoSrc)"
      :src="videoSrc"
      :poster="image?.value?.src"
      :preload="videoOptions.preload"
      :autoplay="videoOptions.autoplay"
      :loop="videoOptions.loop"
      :controls="videoOptions.controls"
      :show-big-button="showPlayButton"
      :as-bg="isBackground || !!ratio"
      ref="playerRef"
    />
    <picture v-else class="e-adaptive-media__picture">
      <source v-if="desktopImageSrc" media="(min-width: 600px)" :srcset="desktopImageSrc" />
      <img
        class="e-adaptive-media__image"
        :src="mobileImageSrc || desktopImageSrc"
        :alt="imageAlt"
        :loading="priority || !lazyLoad ? 'eager' : 'lazy'"
        :fetchpriority="priority ? 'high' : null"
      />
    </picture>

    <div class="e-adaptive-media__content" v-if="$slots.default">
      <slot />
    </div>
  </div>
</template>

<script>
/**
 * @typedef AdaptiveMedia
 * @property {{
 *   mobileImage: ImageField,
 *   mobileVideo: SimpleField,
 *   mobileCaptions: SimpleField,
 *   desktopImage: ImageField,
 *   desktopVideo: SimpleField,
 *   desktopCaptions: SimpleField,
 *   autoplay: CheckField,
 *   hasControls: CheckField
 * }} fields
 * */
import { computed, reactive, toRefs, watch, ref } from 'vue';
import _ from 'lodash';
import useDevice from '@/hooks/useDevice';

export default {
  name: 'AdaptiveMedia',
  inheritAttrs: false,
  props: {
    /**@type AdaptiveMedia*/
    field: {
      type: Object,
    },
    item: {
      type: Object,
      default: null,
    },
    isBackground: {
      type: Boolean,
      default: false,
    },
    ratio: {
      type: Number,
      default: null,
    },
    options: {
      type: Object,
      default: null,
    },
    showPlayButton: {
      type: Boolean,
      default: true,
    },
    showImageRedius: {
      type: Boolean,
      default: true,
    },
    lazyLoad: {
      type: Boolean,
      default: true,
    },
    /** 首屏关键媒体：跳过懒加载并以高优先级请求 */
    priority: {
      type: Boolean,
      default: false,
    },
    initialDeviceType: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const { deviceState } = useDevice();
    const rootEl = ref(null);
    const playerRef = ref(null);

    const state = reactive({
      /**@type SimpleField|null*/
      video: null,
      image: null,
      captions: null,
    });

    const mediaFields = computed(() => props.field?.fields || props.item?.fields || {});
    const getImageSrc = (field) => field?.value?.src || '';
    // 优先使用 <picture> 原生媒体查询选择图片，SSR 与客户端渲染结果一致，
    // 避免服务端按默认 mobile 输出移动端图片、水合后再换成桌面图导致的重复下载。
    const desktopImageSrc = computed(() => getImageSrc(mediaFields.value.desktopImage));
    const mobileImageSrc = computed(() => getImageSrc(mediaFields.value.mobileImage));
    const imageAlt = computed(
      () =>
        mediaFields.value.desktopImage?.value?.alt ||
        mediaFields.value.mobileImage?.value?.alt ||
        ''
    );

    const getImageRatio = (field) => {
      if (props.ratio && !isNaN(props.ratio)) return Number(props.ratio);
      const { width, height } = field?.value || {};
      return width && height ? width / height : null;
    };
    const rootStyle = computed(() => {
      const result = {};
      const formatRatio = (value) => (value ? value.toFixed(6) : null);
      const selectedRatio = getImageRatio(state.image);
      const mobileRatio = getImageRatio(mediaFields.value.mobileImage);
      const desktopRatio = getImageRatio(mediaFields.value.desktopImage);

      if (selectedRatio) result['--ratio'] = formatRatio(selectedRatio);
      if (mobileRatio) result['--ratio-mobile'] = formatRatio(mobileRatio);
      if (desktopRatio) result['--ratio-desktop'] = formatRatio(desktopRatio);
      return result;
    });

    const videoOptions = computed(() => {
      const baseOptions = {
        controls: props.field?.fields?.hasControls?.value || false,
        autoplay: props.field?.fields?.autoplay?.value || false,
        // muted: props.field?.fields?.autoplay?.value ? true : false,
        muted: false,
        loop: props.field?.fields?.autoplay?.value,
        poster: state.image?.value?.src,
        sources: [
          {
            type: 'video/mp4',
            src: state.video?.value?.src || state.video?.value?.href || '',
          },
        ],
      };

      // 濞ｈ濮炵€涙绠锋潪銊╀壕闁板秶鐤?
      if (state.captions?.value?.src) {
        baseOptions.tracks = [
          {
            src: state.captions.value.src,
            kind: 'captions',
            srclang: 'en',
            label: 'English (Sample)',
            default: true,
            mode: 'showing',
          },
        ];
      }

      return _.merge(baseOptions, props.options);
    });

    const videoSrc = computed(() => state.video?.value?.src || state.video?.value?.href || '');

    const judgeMedia = () => {
      const fields = props.field?.fields || props.item?.fields || {};
      const {
        mobileVideo,
        mobileImage,
        mobileCaptions,
        desktopVideo,
        desktopImage,
        desktopCaptions,
      } = fields;

      let video, image, captions;

      const deviceType =
        typeof window === 'undefined' && props.initialDeviceType
          ? props.initialDeviceType
          : deviceState.deviceType;

      switch (deviceType) {
        case 'mobile':
          video = mobileVideo;
          image = mobileImage;
          captions = mobileCaptions;
          break;
        default:
          video = desktopVideo;
          image = desktopImage;
          captions = desktopCaptions;
          break;
      }

      // 娴兼ê鍘涚痪褔鈧槒绶敍姘洤閺嬫粍婀佺憴鍡涱暥鐏忚京鏁ょ憴鍡涱暥閿涘苯鎯侀崚娆戞暏閸ュ墽澧?
      state.video = video && video.value ? video : null;
      state.image = image && image.value ? image : null;
      state.captions = captions;
    };

    watch(
      () => deviceState.deviceType,
      () => {
        judgeMedia();
      },
      { immediate: true }
    );

    watch(
      () => props.field,
      () => {
        judgeMedia();
      },
      {
        immediate: true,
        deep: true,
      }
    );

    watch(
      () => props.item,
      () => {
        judgeMedia();
      },
      {
        immediate: true,
        deep: true,
      }
    );

    return {
      rootEl,
      playerRef,
      ...toRefs(state),
      rootStyle,
      videoOptions,
      videoSrc,
      desktopImageSrc,
      mobileImageSrc,
      imageAlt,
    };
  },
};
</script>

<style lang="scss">
@import '../styles/variables';
@import '../styles/functions';
@import '../styles/mixins';

.e-adaptive-media {
  $c: &;
  // 与 JS 设备断点 BREAKPOINTS.mobile(600) 保持一致，保证广告图与占位比例同步切换
  aspect-ratio: var(--ratio-mobile, var(--ratio));
  position: relative;
  width: 100%;
  height: inherit;
  overflow: hidden;

  @media (min-width: 600px) {
    aspect-ratio: var(--ratio-desktop, var(--ratio));
  }

  &.as-bg {
    width: 100%;
    height: 100%;

    #{$c}__picture {
      display: block;
      width: 100%;
      height: 100%;
    }

    #{$c}__image {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: var(--object-position, center);
    }

    #{$c}__video {
      width: 100%;
      height: 100%;

      > video {
        width: 100%;
        height: 100%;
        object-fit: cover;
        object-position: var(--object-position, center);
      }
    }
  }

  &__picture {
    display: block;
    width: 100%;
  }

  &__content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  &__image {
    width: 100%;
    height: auto;
  }

  &.controls {
    #{$c}__content {
      height: calc(100% - 40px);
    }
  }

  &.has-radius {
    #{$c}__image,
    #{$c}__video {
      border-radius: 0;
      overflow: hidden;
    }
  }

  @include tablet-landscape {
    &.has-radius {
      #{$c}__image,
      #{$c}__video {
        border-radius: 0;
      }
    }
  }
}
</style>
